import { Divider } from '@adobe/react-spectrum';
import React from 'react';
import { useFocusRing, useId, useLink } from 'react-aria';
import styled from 'styled-components';

import { WithSkeleton } from '@exchange-frontends/components';

/**
 * FeaturedProductCard component that displays a product card with image, title, and subtitle
 * @component
 * @param {object} props - Component props
 * @param {string} props.title - The title text to display on the card
 * @param {string} props.subtitle - The subtitle text to display on the card
 * @param {string} props.imageLink - The URL of the image to display
 * @param {string} [props.imageAlt=''] - Alt text for the image
 * @param {string} props.link - The URL the card links to when clicked
 * @param {boolean} props.loading - Whether the card is in a loading state
 * @param {object} [props.styleProps={}] - Custom style props for different parts of the card
 * @param {object} [props.styleProps.container] - Style props for the main container
 * @param {object} [props.styleProps.container.$cardMinWidth] - Minimum width of the card (default: 240px)
 * @param {object} [props.styleProps.container.$cardWidth] - Width of the card
 * @param {object} [props.styleProps.container.$cardHeight] - Height of the card
 * @param {object} [props.styleProps.container.$cardBackground] - Background color of the card (default: #ffffff)
 * @param {object} [props.styleProps.container.$cardBorderRadius] - Border radius of the card (default: 4px)
 * @param {object} [props.styleProps.container.$cardBorderColor] - Border color of the card (default: var(--spectrum-gray-200))
 * @param {object} [props.styleProps.container.$cardBorderColorHover] - Border color on hover (default: var(--spectrum-gray-300))
 * @param {object} [props.styleProps.container.$cardBorderWidth] - Border width of the card (default: 1px)
 * @param {object} [props.styleProps.preview] - Style props for the image preview container
 * @param {object} [props.styleProps.preview.$cardPreviewHeight] - Height of the preview container (default: 200px)
 * @param {object} [props.styleProps.preview.$cardPreviewBackground] - Background of the preview container (default: linear gradient)
 * @param {object} [props.styleProps.body] - Style props for the card body
 * @param {object} [props.styleProps.body.$cardBodyPadding] - Padding of the card body (default: 16px)
 * @param {object} [props.styleProps.title] - Style props for the title text
 * @param {object} [props.styleProps.title.$cardTitleFontSize] - Font size of the title (default: var(--spectrum-global-dimension-static-font-size-200))
 * @param {object} [props.styleProps.title.$cardTitleFontWeight] - Font weight of the title (default: var(--spectrum-global-font-weight-bold))
 * @param {object} [props.styleProps.title.$cardTitleColor] - Color of the title (default: var(--spectrum-global-color-static-gray-900))
 * @param {object} [props.styleProps.subtitle] - Style props for the subtitle text
 * @param {object} [props.styleProps.subtitle.$cardSubtitleFontSize] - Font size of the subtitle (default: var(--spectrum-global-dimension-static-font-size-75))
 * @param {object} [props.styleProps.subtitle.$cardSubtitleColor] - Color of the subtitle (default: var(--spectrum-global-color-static-gray-600))
 * @param {string} [props.dataTestID] - Test ID for testing purposes
 */
export const FeaturedProductCard = ({
  title,
  subtitle,
  imageLink,
  imageAlt = '',
  link,
  loading,
  styleProps = {},
  dataTestID,
}) => {
  const ref = React.useRef(null);
  const titleId = useId();
  const subtitleId = useId();
  const { linkProps } = useLink({ href: link, 'aria-labelledby': titleId, 'aria-describedby': subtitleId }, ref);
  const { isFocusVisible, focusProps } = useFocusRing();
  const {
    container: containerStyleProps,
    preview: previewStyleProps,
    body: bodyStyleProps,
    title: titleStyleProps,
    subtitle: subtitleStyleProps,
  } = styleProps;

  return (
    <CardContainer
      data-testid={dataTestID}
      data-launchid="featured-products"
      aria-busy={loading}
      $isFocusVisible={isFocusVisible}
      {...containerStyleProps}
      {...linkProps}
      {...focusProps}
      ref={ref}
    >
      <WithSkeleton shouldRender={!loading} width="100%" height={200} style={{ lineHeight: 'unset', borderRadius: 0 }}>
        <CardPreviewContainer {...previewStyleProps}>
          <CardPreviewImage src={imageLink} alt={imageAlt} />
        </CardPreviewContainer>
      </WithSkeleton>
      <Divider size="S" />
      <CardBody {...bodyStyleProps}>
        <CardTitle {...titleStyleProps} id={titleId}>
          <WithSkeleton shouldRender={!loading} width={200}>
            {title}
          </WithSkeleton>
        </CardTitle>
        <CardSubtitle id={subtitleId} {...subtitleStyleProps}>
          <WithSkeleton shouldRender={!loading} width={150}>
            {subtitle}
          </WithSkeleton>
        </CardSubtitle>
      </CardBody>
    </CardContainer>
  );
};

const CardContainer = styled.a`
  --xchg-min-card-width: ${({ $cardMinWidth }) => $cardMinWidth};
  --xchg-card-width: ${({ $cardWidth }) => $cardWidth};
  --xchg-card-height: ${({ $cardHeight }) => $cardHeight};
  --xchg-card-background: ${({ $cardBackground }) => $cardBackground};
  --xchg-card-border-radius: ${({ $cardBorderRadius }) => $cardBorderRadius};
  --xchg-card-border-color: ${({ $cardBorderColor }) => $cardBorderColor};
  --xchg-card-border-color-hover: ${({ $cardBorderColorHover }) => $cardBorderColorHover};
  --xchg-card-border-width: ${({ $cardBorderWidth }) => $cardBorderWidth};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: auto;
  cursor: pointer;
  min-width: var(--xchg-min-card-width, 240px);
  width: var(--xchg-card-width, 0);
  height: var(--xchg-card-height);
  background-color: var(--xchg-card-background, #ffffff);
  border-radius: var(--xchg-card-border-radius, 4px);
  border-color: var(--xchg-card-border-color, var(--spectrum-gray-200));
  border-width: var(--xchg-card-border-width, 1px);
  border-style: solid;
  text-decoration: none;
  overflow: hidden;
  position: relative;
  outline: ${({ $isFocusVisible }) => ($isFocusVisible ? '2px solid var(--spectrum-alias-focus-ring-color)' : 'none')};
  outline-offset: 2px;

  &:hover {
    border-color: var(--xchg-card-border-color-hover, var(--spectrum-gray-300));
  }
`;

const CardPreviewContainer = styled.div`
  --xchg-card-preview-height: ${({ $cardPreviewHeight }) => $cardPreviewHeight};
  --xchg-card-preview-background: ${({ $cardPreviewBackground }) => $cardPreviewBackground};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: var(--xchg-card-preview-height, 200px);
  background: var(--xchg-card-preview-background, linear-gradient(0deg, rgb(179, 10, 0) 0%, rgb(247, 13, 0) 100%));
`;

const CardPreviewImage = styled.img`
  object-fit: contain;
  width: 100%;
  display: block;
`;

const CardBody = styled.div`
  --xchg-card-body-padding: ${({ $cardBodyPadding }) => $cardBodyPadding};
  padding: var(--xchg-card-body-padding, 16px);
`;

const CardTitle = styled.div`
  --xchg-card-title-font-size: ${({ $cardTitleFontSize }) => $cardTitleFontSize};
  --xchg-card-title-font-weight: ${({ $cardTitleFontWeight }) => $cardTitleFontWeight};
  --xchg-card-title-color: ${({ $cardTitleColor }) => $cardTitleColor};
  font-size: var(--xchg-card-title-font-size, var(--spectrum-global-dimension-static-font-size-200));
  font-weight: var(--xchg-card-title-font-weight, var(--spectrum-global-font-weight-bold));
  color: var(--xchg-card-title-color, var(--spectrum-global-color-static-gray-900));
`;

const CardSubtitle = styled.div`
  --xchg-card-subtitle-font-size: ${({ $cardSubtitleFontSize }) => $cardSubtitleFontSize};
  --xchg-card-subtitle-color: ${({ $cardSubtitleColor }) => $cardSubtitleColor};
  font-size: var(--xchg-card-subtitle-font-size, var(--spectrum-global-dimension-static-font-size-75));
  color: var(--xchg-card-subtitle-color, var(--spectrum-global-color-static-gray-600));
`;

export default FeaturedProductCard;
